import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import ViewQuiltOutlinedIcon from "@mui/icons-material/ViewQuiltOutlined";
import { Box, Button, CircularProgress, Divider, IconButton, Stack, Tooltip } from '@mui/material';
import { Theme } from '@mui/material/styles';
import axios from 'axios';
import { find, noop } from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { ReactElement } from 'react';
import { NavigateFunction } from 'react-router-dom';
import AlertDialog from '../../components/app/alert-dialog';
import TypedAvatar from '../../components/app/TypedAvatar';
import MaterialQuoteView from '../../components/discovery/material-quote-view';
import OverviewPanel, { IOverviewSection } from '../../components/general/OverviewPanel';
import { withRouter } from '../../components/hoc/WithRouter';
import { APP_NOTIFICATION_COMPONENT_STATIC_TEXTS, ARTIFACT_TYPE_INTERVIEW_TAB_MAP, NOTIFICATION_OVERVIEW_TEXTS, NOTIFICATION_TYPES } from '../../constants';
import { baseAtom, downloadExportInterviewSignedURL, ICohort, ITeam, IUser, selectInterview, selectInterviewWithInsight, setSelectedArtifactType } from '../../stores/jotai/base';
import { setHypothesisIdToshowCommentPopup } from '../../stores/jotai/hypothesisToolbar';
import { InterviewArray } from '../../stores/jotai/interviews/interviews';
import { getTypeIcon } from '../../util/getTypeIcon';
import { getTabForInsight } from '../../util/insight';
import * as notificationUtils from '../../util/notification_utils';

const notificationWrapperStyle = (theme: Theme)=> ({
    alignItems: "flex-start",
    cursor: "pointer",
    padding: "16px 8px",
    borderRadius: "4px",
    fontSize: "14px",
    color: "rgba(0, 0, 0, 0.7)",
    gap: "12px",
    transition: "background-color 0.1s ease",
    ":hover": {
        backgroundColor: theme.custom.hoverOptionColor,
    },
});

const notificationContentStyle = {
    display: "inline-block",
    flexGrow: 1,
};

interface INotificationMessage {
    cohortID: string;
    created: string;
    id: string;
    orgID: string;
    payload: any;
    seen: number;
    seenDate: string;
    teamID: string;
    type: string;
    userID: string;
}

interface IProps {
    handleMarkNotificationAsRead: Function;
    handleMarkNotificationAsUnread: Function;
    messageData: INotificationMessage;
    interviews: InterviewArray
    cohorts: ICohort[];
    teams: ITeam[];
    user: IUser;
    base?: any;
    setBase?: Function;
    isNotificationPopoverOpen: boolean;
    setHypothesisToolbarAtomState?: Function;
    closeNotificationPopover?: Function;
    switchTeam?: Function;
    navigate?: NavigateFunction
    findings: {[key: string]: any}
    theme?: Theme
};

interface IState {
    isChangingReadStatus: boolean;
    showOverviewPopover: boolean;
    showTeamNavigationConfirmation: boolean;
}
class NotificationMessage extends React.Component<IProps, IState> {
    props: IProps;

    state = {
        isChangingReadStatus: false,
        showOverviewPopover: false,
        showTeamNavigationConfirmation: false,
    };

    public static defaultProps = {
        handleMarkNotificationAsRead: noop,
        handleMarkNotificationAsUnread: noop,
        messageData: null,
        cohorts: [],
        teams: [],
        user: null,
        base: null,
        isNotificationPopoverOpen: false,
        setBase: noop,
        setHypothesisToolbarAtomState: noop,
        closeNotificationPopover: noop,
        switchTeam: noop,
    };

    isTeamChangedForSelectedNotification = (prevProps) => {
        return (
            prevProps.base?.team?.groupID !== this.props?.base?.team?.groupID
        );
    };

    shouldCloseNotificationPopover = (isNotificationPopoverOpen, prevProps) => {
        return (
            prevProps.isNotificationPopoverOpen !== isNotificationPopoverOpen &&
            !isNotificationPopoverOpen
        );
    };

    componentDidUpdate(
        prevProps: Readonly<IProps>,
        prevState: Readonly<IState>,
        snapshot?: any
    ): void {
        const { isNotificationPopoverOpen } = this.props;

        if (
            this.shouldCloseNotificationPopover(
                isNotificationPopoverOpen,
                prevProps
            )
        ) {
            this.setState({
                showOverviewPopover: isNotificationPopoverOpen,
            });

            if (this.isTeamChangedForSelectedNotification(prevProps)) {
                this.navigateToNotificationSource();
            }
        }
    }

    // Check all the possible locations that a "type" can exist
    getType = () => {
        const { messageData } = this.props;

        return notificationUtils.getNotificationType(messageData);
    };

    getPayload = () => {
        const { messageData } = this.props;

        return notificationUtils.getNotificationPayload(messageData);
    };

    getPayloadData = (type: string, data: any): any => {
        return notificationUtils.getNotificationPayloadData(type, data);
    };

    getGroupName = (groupID: string) => {
        const { cohorts, teams } = this.props;

        const possibleCohort = find(cohorts, (cohort) => {
            return cohort?.id === groupID;
        });
        const possibleTeam = teams[groupID];

        if (possibleCohort) return possibleCohort?.name;
        if (possibleTeam) return possibleTeam?.name;

        return null;
    };

    renderNotificationOverview = () => {
        const { base } = this.props;
        const type = this.getType();
        const typeData = this.getPayload();        
        let overviewSections: IOverviewSection[] = [];

        if (type === NOTIFICATION_TYPES.COMMENT) {
            const entrySegment =
                typeData?.entrySegment || typeData?.data?.entrySegment;
            const entryTitle =
                typeData?.entryContent || typeData?.data?.entryContent;
            const commentContent = typeData?.data?.content;

            overviewSections = [
                {
                    title: entrySegment,
                    content: entryTitle,
                    hasDivider: true,
                },
                {
                    title: NOTIFICATION_OVERVIEW_TEXTS.COMMENT_SECTION_TITLE,
                    content: commentContent,
                },
            ];
        } else if (type === NOTIFICATION_TYPES.INSIGHT) {     
            const insightID = typeData?.findingID || typeData?.data?.findingID;
            const artifactID = typeData?.artifactID || typeData?.data?.artifactID;
            const feedbackContent = typeData?.comment || typeData?.data?.comment || null;
            const insightQuote =
                typeData?.quote ||
                (this.props?.findings ? this.props?.findings[insightID]?.quote : undefined)
            const interviewee =
                typeData?.interviewee || typeData?.data?.interviewee || "Unknown";

            overviewSections = [
                {
                    title: NOTIFICATION_OVERVIEW_TEXTS.INTERVIEW_SECTION_TITLE,
                    content: interviewee,
                    hasDivider: true,
                },
                {
                    title: NOTIFICATION_OVERVIEW_TEXTS.QUOTE_SECTION_TITLE,
                    content: (
                        <Box
                            component="span"
                            sx={{ fontStyle: "italic" }}
                        >
                            <MaterialQuoteView
                                artifactID={artifactID}
                                selection={insightQuote}
                                quouteCollapseLength={Infinity}
                            />
                        </Box>
                    ),
                },
                ...(!!feedbackContent ? [{
                    title: NOTIFICATION_OVERVIEW_TEXTS.COMMENT_SECTION_TITLE,
                    content: feedbackContent,
                }] : []),
            ];
        } else if (type === NOTIFICATION_TYPES.AI_INSIGHT) {
            const interviewee =
                typeData?.interviewee ||
                typeData?.data?.interviewee ||
                "Unknown";
            const insightCount = typeData?.insightCount || 0;
            const interviewer = typeData?.interviewer || "Unknown";
            const interviewDate = typeData?.interviewDate || "Unknown";

            overviewSections = [
                {
                    title: "Interviewer",
                    content: `${interviewer}`,
                },
                {
                    title: "Interviewee",
                    content: `${interviewee}`,
                },
                {
                    title: "New Insights",
                    content: `${insightCount}`,
                },
                {
                    title: "Interview Date",
                    content: moment(interviewDate).format("MM/DD/YY"),
                },
            ];
        }

        return <OverviewPanel sections={overviewSections} />;
    }

    onOverviewOpen = () => {
        const { isNotificationPopoverOpen, messageData } = this.props;
		const type = notificationUtils.getNotificationType(messageData);

        if (type !== NOTIFICATION_TYPES.EXPORT_TEAM_INTERVIEW) {
            this.setState({
                showOverviewPopover: !!isNotificationPopoverOpen,
            });
        }
    }

    onOverviewClose = () => {
        this.setState({
            showOverviewPopover: false,
        });
    }

    navigateToCanvasComment = (hypothesisID) => {
        const { setBase, setHypothesisToolbarAtomState, switchTeam , navigate} =
            this.props;
        const notificaitonTeamID = this.getNotificationTeamID();
        navigate('/canvas');
        setHypothesisIdToshowCommentPopup(
            setHypothesisToolbarAtomState,
            hypothesisID
        );

        switchTeam(notificaitonTeamID, {});
    };

    navigateToInterviewInsight = (interviewID, insightID) => {
        const { base, setBase, switchTeam } = this.props;
        const notificaitonTeamID = this.getNotificationTeamID();
        const insight = this.props?.findings[insightID];

        const selectedTab = getTabForInsight(insight);

        setSelectedArtifactType(setBase, selectedTab);

        if (!!insight) {
            selectInterviewWithInsight(setBase, interviewID, insight, this.props?.interviews);
            this.props.navigate('/discovery');
        } else {
            selectInterview(setBase, interviewID, this.props?.interviews);
            this.props.navigate('/discovery');
        }

        switchTeam(notificaitonTeamID, {
            selectedFinding: insightID,
            selectedInterview: interviewID,
            selectedArtifactType: selectedTab,
        });
    };

	handleExportClick = (data: string) => {
		// Use the given data to get a signed artifact URL to give
		// the user
		axios.get(data).then((res) => {
			// @ts-ignore
			const resultingLocation = res?.text;

			// @ts-ignore
			(typeof window !== "undefined" ? window : {}).open(
				resultingLocation,
				"_blank"
			);
		});
	};

    handleAIInsightClick = (payload: any) => {
        const { setBase, switchTeam } = this.props;
        const { interviewID, artifactType } = payload;
        const selectedTab = ARTIFACT_TYPE_INTERVIEW_TAB_MAP[artifactType];
        const notificaitonTeamID = this.getNotificationTeamID();

        selectInterview(setBase, interviewID, this.props?.interviews);
        setSelectedArtifactType(setBase, selectedTab);
        this.props.navigate('/discovery');

        switchTeam(notificaitonTeamID, {
            selectedInterview: interviewID,
            selectedArtifactType: selectedTab,
        });
    };

	navigateToNotificationSource = () => {
        const { handleMarkNotificationAsRead, messageData,base } = this.props;
        const isRead = messageData.seen === 1;

        if (!isRead) {
            handleMarkNotificationAsRead();
        }

		const typeData = notificationUtils.getNotificationPayload(messageData);
    
		const type = notificationUtils.getNotificationType(messageData);
		const hypothesisID = typeData?.data?.entryID || null;
		const interviewID =
			typeData?.interviewID || typeData?.data?.interviewID || null;
		const insightID = typeData?.findingID || typeData?.data?.findingID || null;

		if (type === NOTIFICATION_TYPES.COMMENT) {
            this.navigateToCanvasComment(hypothesisID);
        } else if (type === NOTIFICATION_TYPES.INSIGHT) {
            this.navigateToInterviewInsight(interviewID, insightID);
        } else if (type === NOTIFICATION_TYPES.EXPORT_TEAM_INTERVIEW) {
            downloadExportInterviewSignedURL(typeData.signedUrl,base?.team?.name);
        } else if (type === NOTIFICATION_TYPES.AI_INSIGHT) {
            this.handleAIInsightClick(typeData);
        }
	};

	handleNotificationClick = () => {
		const { base, closeNotificationPopover, messageData } = this.props;
		const type = notificationUtils.getNotificationType(messageData);
		const currentTeamID = base?.team?.groupID;
		const notificaitonTeamID = this.getNotificationTeamID();

		closeNotificationPopover();

        if (
            currentTeamID !== notificaitonTeamID &&
            type !== NOTIFICATION_TYPES.EXPORT_TEAM_INTERVIEW
        ) {
            this.setState({
                showTeamNavigationConfirmation: true,
            });
        } else {
            this.navigateToNotificationSource();
        }
	};

	renderTeamNavigationConfirmation = () => {
        const { switchTeam } = this.props;
		const { showTeamNavigationConfirmation } = this.state;
        const notificaitonTeamID = this.getNotificationTeamID();
        const notificationTeamName = this.renderTeamName(notificaitonTeamID);
        const notificationType = this.getType();

		return (
			<AlertDialog
				open={showTeamNavigationConfirmation}
				title="Want to navigate?"
				description={
					<>
						This {notificationType} belongs to <b>{notificationTeamName}</b>.
						Would you like to switch to <b>{notificationTeamName}</b>?
					</>
				}
				confirmButton={
					<Button
						variant="contained"
						onClick={() => {
                            this.navigateToNotificationSource();
                        }}
					>
						Yes
					</Button>
				}
				abortButton={
					<Button
						variant="outlined"
						onClick={() =>
							this.setState({ showTeamNavigationConfirmation: false })
						}
					>
						No
					</Button>
				}
			/>
		);
	};

    // General layout wrapper
    renderWrapper = (children) => {
        const { showOverviewPopover } = this.state;
        const { theme } = this.props;
        return (
            <>
                <Tooltip
                    classes={{
                        popper: "NotificationOverviewTooltip",
                    }}
                    open={showOverviewPopover}
                    title={this.renderNotificationOverview()}
                    onOpen={this.onOverviewOpen}
                    onClose={this.onOverviewClose}
                    placement="left"
                    sx={{ pointerEvents: "auto !important" }}
                    slotProps={{
                        popper: {
                            sx: {
                                pointerEvents: "auto !important",
                            },
                        },
                        tooltip: {
                            sx: {
                                width: "300px",
                                boxShadow:
                                    "rgba(0, 0, 0, 0.1) 1px 0px 6px 1px, rgba(0, 0, 0, 0.1) 0px 4px 11px !important",
                                background: theme?.custom?.white,
                                color: theme?.custom?.text,
                                ".MuiTooltip-arrow": {
                                    color: theme?.custom?.white,
                                },
                            },
                        },
                    }}
                >
                    <Stack
                        className="NotificationWrapper"
                        direction="row"
                        aria-haspopup="true"
                        sx={{
                            ...notificationWrapperStyle(theme),
                            gap: "14px",
                        }}
                        onClick={(event) => {
                            event.stopPropagation();
                            this.handleNotificationClick();
                        }}
                    >
                        {children}
                    </Stack>
                </Tooltip>
                <Divider
                    sx={{ margin: "6px 0", borderColor: theme?.custom?.disabledColor }}
                />
                {this.renderTeamNavigationConfirmation()}
            </>
        );
    };

    getNotificationTeamID = () => {
        const { messageData } = this.props;

        if (messageData?.teamID !== undefined) {
            return messageData?.teamID;
        }

        if (
            messageData?.payload !== undefined &&
            messageData?.payload?.teamID !== undefined
        ) {
            return messageData?.payload?.teamID;
        }

        if (
            messageData?.payload !== undefined &&
            messageData?.payload?.groupID !== undefined
        ) {
            return messageData?.payload?.groupID;
        }

        if (messageData?.payload !== undefined) {
            const payload = JSON.parse(messageData?.payload);
            return payload?.groupID;
        }

        return "";
    };

    handleChangeReadStatus = (event: React.MouseEvent) => {
        const {
            handleMarkNotificationAsUnread,
            handleMarkNotificationAsRead,
            messageData,
        } = this.props;
        const isRead = messageData.seen === 1;

        event.stopPropagation();

        this.setState({
            isChangingReadStatus: true,
        });

        if (isRead) {
            handleMarkNotificationAsUnread(() => {
                this.setState({
                    isChangingReadStatus: false,
                });
            });
        } else {
            handleMarkNotificationAsRead(() => {
                this.setState({
                    isChangingReadStatus: false,
                });
            });
        }
    };

    renderNotificationTypeIcon = () => {
        const type = this.getType();

        if (type === NOTIFICATION_TYPES.INSIGHT) {
            return <RecordVoiceOverOutlinedIcon />;
        } else if (type === NOTIFICATION_TYPES.EXPORT_TEAM_INTERVIEW) {
            return <FileDownloadOutlinedIcon />;
        }

        return <ViewQuiltOutlinedIcon />;
    }

    renderNotificationAvatar = () => {
        const { members } = this.props?.base;
        const typeData = this.getPayload();
        const type = this.getType();
        const userID = typeData?.userID || typeData?.data?.userID;
        const userName = typeData?.userName || typeData?.data?.userName;
        const userPhotoURL = members.find(member => member.userID === userID)?.photoURL || null;

        return type === NOTIFICATION_TYPES.AI_INSIGHT ? (
            <MemoryOutlinedIcon
                color="primary"
                sx={{
                    width: 35,
                    height: 35,
                }}
            />
        ) : (
            <TypedAvatar
                user={{ name: userName, photoURL: userPhotoURL }}
                typeIcon={getTypeIcon(type)}
                tooltipTitle={userName}
            />
        );
    };

    renderTeamName = (notificationTeamID: string) => {
        const { base } = this.props;
        const currentTeamID = base?.team?.teamID;

        if (notificationTeamID !== currentTeamID) {
            const notificaitonTeamName = this.getGroupName(notificationTeamID);

            return notificaitonTeamName;
        }

        return null;
    };

    renderNotificationContent = () => {
        const { messageData } = this.props;
        const { created } = messageData;
        const type = this.getType();
        const typeData = this.getPayload();
        const { groupName } = typeData;
        const userName: string = typeData?.userName || typeData?.data?.userName;
        const firstName: string = (userName ? userName : "").split(" ")[0] || null;
        const dateDifferenceFromNow = moment(created).fromNow();
        const notificaitonTeamID = this.getNotificationTeamID();
        const notificationTeamName = this.renderTeamName(notificaitonTeamID);

        let content = "";

        if (!!firstName) {
            content = `${firstName} posted a canvas comment.`;
        } else {
            content = "A Canvas Comment was posted.";
        }

        if (type === NOTIFICATION_TYPES.INSIGHT) {
            if (!!firstName) {
                content = `${firstName} posted interview feedback.`;
            } else {
                content = "Interview feedback was posted.";
            }
        } else if (type === NOTIFICATION_TYPES.EXPORT_TEAM_INTERVIEW) {
            content = `Interview export is ready for ${groupName}`;
        } else if (type === NOTIFICATION_TYPES.AI_INSIGHT) {
            content = "AI Generated Insights are Ready for Review.";
        }
        

        return (
            <Stack style={notificationContentStyle}>
                <Stack
                    direction="row"
                    sx={{ alignItems: "flex-start" }}
                >
                    <Stack
                        sx={{
                            flexGrow: 1,
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: "inline-block",
                                fontWeight: 600,
                                marginRight: "8px",
                            }}
                        >
                            {content}
                        </Box>
                        <Box
                            sx={{
                                color: "rgba(0, 0, 0, 0.5)",
                                fontSize: "12px",
                                marginTop: "2px",
                            }}
                        >
                            {dateDifferenceFromNow}
                            {!!notificationTeamName ? ` | ${notificationTeamName}` : null}
                        </Box>
                    </Stack>
                    {this.renderReadButton()}
                </Stack>
            </Stack>
        );
    };

    renderReadButton = () => {
        const { isChangingReadStatus } = this.state;
        const { seen } = this.props?.messageData;
        const isRead = seen === 1;
        const { theme } = this.props;
        return (
            <Stack
                direction="row"
                sx={{
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Tooltip
                    title={
                        isRead
                            ? APP_NOTIFICATION_COMPONENT_STATIC_TEXTS
                                    .MARK_AS_UNREAD_BUTTON.TOOLTIP
                            : APP_NOTIFICATION_COMPONENT_STATIC_TEXTS.MARK_AS_READ_BUTTON
                                    .TOOLTIP
                    }
                    enterDelay={0}
                >
                    {isChangingReadStatus ? (
                        <CircularProgress size={20} />
                    ) : (
                        <IconButton
                            className="NotificationSeenButton"
                            size="small"
                            color="primary"
                            sx={{
                                width: "20px",
                                height: "20px",
                                boxShadow: `0 0 1px 1px none`,
                                "&.NotificationSeenButton:hover": {
                                    boxShadow: `0 0 1px 1px transparent`,
                                },
                            }}
                            onClick={this.handleChangeReadStatus}
                        >
                            <FiberManualRecordIcon
                                sx={{
                                    fontSize: "14px",
                                    color: isRead ? theme?.custom?.borderGrey : "#05bd8c",
                                }}
                            />
                        </IconButton>
                    )}
                </Tooltip>
            </Stack>
        );
    };

    render(): ReactElement {
        const type = this.getType();

        if (type === "") return null;

        return this.renderWrapper(
            <>
                {this.renderNotificationAvatar()}
                {this.renderNotificationContent()}
            </>
        );
    }
}
export default withRouter(NotificationMessage)